import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ViewChild, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import * as XLSX from 'xlsx';
import { MatSpinnerOverlayComponent } from '../../components/mat-spinner-overlay/mat-spinner-overlay.component';
import { SnackBarService } from '../../services/snack-bar-service.service';
import { CriarMasterComponent } from '../criar-master/criar-master.component';
import { EditarMasterComponent } from '../editar-master/editar-master.component';
import { AdmRedePosto } from '../models';
import { AdmRedePostosService } from '../services/adm-rede-postos.service';
import { TabelaLogsComponent } from '../tabela-logs/tabela-logs.component';
import { TabelaPostosComponent } from '../tabela-postos/tabela-postos.component';
import { TabelaUsuariosComponent } from '../tabela-usuarios/tabela-usuarios.component';
import { StringNormalizer } from '../utils';

/**
 * Component for managing macro posts (admin network posts)
 */
@Component({
  selector: 'app-gerenciar-macro-posto',
  templateUrl: './gerenciar-macro-posto.component.html',
  styleUrls: ['./gerenciar-macro-posto.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    MatTableModule,
    MatSpinnerOverlayComponent,
    MatPaginatorModule,
    MatSortModule,
    MatFormFieldModule,
    MatInputModule,
    MatBadgeModule,
    MatSelectModule,
    MatExpansionModule,
    TabelaUsuariosComponent,
    TabelaPostosComponent,
  ],
})
export class GerenciarMacroPostoComponent implements AfterViewInit {
  // ==================== Dependencies ====================
  private readonly dialog = inject(MatDialog);
  private readonly service = inject(AdmRedePostosService);
  private readonly snack = inject(SnackBarService);

  // ==================== ViewChild ====================
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  // ==================== State ====================
  public loading: boolean = false;
  private _masters: AdmRedePosto[] = [];
  private _selectedMaster: AdmRedePosto | undefined = undefined;
  private _expandedMasterId: string | undefined = undefined;

  // ==================== Getters ====================
  get masters(): AdmRedePosto[] {
    return this._masters;
  }

  get selectedMaster(): AdmRedePosto | undefined {
    return this._selectedMaster;
  }

  get expandedMasterId(): string | undefined {
    return this._expandedMasterId;
  }

  // ==================== Data Source ====================
  readonly dataSource = new MatTableDataSource<AdmRedePosto>([]);

  // ==================== Forms ====================
  readonly filterForm = new FormGroup({
    codigoRede: new FormControl(''),
    nomeRede: new FormControl(''),
  });

  // ==================== Lifecycle ====================
  constructor() {
    this.loadMasters();
  }

  ngAfterViewInit(): void {
    this.setupDataSource();
  }

  // ==================== Setup Methods ====================
  private setupDataSource(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.configureSorting();
    this.configureFiltering();
  }

  private configureSorting(): void {
    this.dataSource.sortingDataAccessor = (
      data: AdmRedePosto,
      sortHeaderId: string
    ) => {
      switch (sortHeaderId) {
        case 'nome':
          return StringNormalizer.normalizeForSorting(data.nome);
        case 'id':
          return data.idAdmRedePosto;
        default:
          return (data as any)[sortHeaderId] || '';
      }
    };
  }

  private configureFiltering(): void {
    this.dataSource.filterPredicate = (
      data: AdmRedePosto,
      filter: string
    ): boolean => {
      const filters = JSON.parse(filter);
      const matchId =
        !filters.codigoRede ||
        data.idAdmRedePosto
          .toLowerCase()
          .includes(filters.codigoRede.toLowerCase());
      const matchNome =
        !filters.nomeRede ||
        StringNormalizer.normalizeForSearch(data.nome).includes(
          StringNormalizer.normalizeForSearch(filters.nomeRede)
        );
      return matchId && matchNome;
    };
  }

  // ==================== Data Loading ====================
  private loadMasters(): void {
    this.loading = true;
    this.service.buscarListaAdmRedePosto().subscribe({
      next: (masters) => {
        this._masters = masters;
        this.dataSource.data = masters;
        this.reconfigureSort();
      },
      error: () => {
        this.loading = false;
        this.snack.flashMessage(
          'Erro na requisição ao carregar as redes',
          ['snackbar-error'],
          'Fechar',
          3000
        );
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  private reconfigureSort(): void {
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
  }

  // ==================== Filter Methods ====================
  applyFilter(): void {
    const { codigoRede, nomeRede } = this.filterForm.value;
    this.dataSource.filter = JSON.stringify({ codigoRede, nomeRede });
    this.dataSource.paginator?.firstPage();
  }

  clearFilter(): void {
    this.filterForm.reset();
    this.applyFilter();
  }

  // ==================== Dialog Methods ====================
  openLogDialog(): void {
    this.dialog.open(TabelaLogsComponent, {
      width: '90%',
      maxWidth: '100vw',
      maxHeight: '90vh',
    });
  }

  criarNovaMaster(): void {
    const dialogRef = this.dialog.open(CriarMasterComponent, {
      width: '40%',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.sucesso) {
        this.loadMasters();
        this.snack.flashMessage(
          'Rede criada com sucesso!',
          ['snackbar-success'],
          'Fechar',
          3000
        );
      }
    });
  }

  editarMacroPosto(master: AdmRedePosto): void {
    const dialogRef = this.dialog.open(EditarMasterComponent, {
      width: '40%',
      data: master,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.sucesso) {
        this.snack.flashMessage(
          'Rede alterada com sucesso!',
          ['snackbar-success'],
          'Fechar',
          3000
        );
        this.loadMasters();
      }
    });
  }

  // ==================== Expansion Methods ====================
  expandirUsuarios(master: AdmRedePosto): void {
    this._selectedMaster = master;
    this._expandedMasterId = master.idAdmRedePosto;
    master.toggleUsuarios();
  }

  expandirPostos(master: AdmRedePosto): void {
    this._selectedMaster = master;
    this._expandedMasterId = master.idAdmRedePosto;
    master.togglePostos();
  }

  // ==================== Update Methods ====================
  atualizarQtdPostos(totalPostos: number): void {
    if (!this._selectedMaster) return;

    this._selectedMaster.qtdPostos = totalPostos;
    this.updateMasterInList(this._selectedMaster);
  }

  atualizarQtdUsuarios(totalUsuarios: number): void {
    if (!this._selectedMaster) return;

    this._selectedMaster.qtdUsuarios = totalUsuarios;
    this.updateMasterInList(this._selectedMaster);
  }

  private updateMasterInList(master: AdmRedePosto): void {
    const index = this._masters.findIndex(
      (m: AdmRedePosto) => m.idAdmRedePosto === master.idAdmRedePosto
    );
    if (index !== -1) {
      this._masters[index] = master;
      this.dataSource.data = [...this._masters];
    }
  }

  onNovoUsuarioCriado(): void {
    // Event handled by totalUsuariosChange
  }

  onRemoverUsuario(): void {
    // Event handled by totalUsuariosChange
  }

  // ==================== Export Methods ====================
  exportToExcel(): void {
    const data = this.prepareExcelData();
    const workSheet = XLSX.utils.json_to_sheet(data);
    const workBook = XLSX.utils.book_new();
    const fileName = this.generateFileName();
    XLSX.utils.book_append_sheet(workBook, workSheet, fileName);
    XLSX.writeFile(workBook, `${fileName}.xlsx`);
  }

  private prepareExcelData(): Array<{
    'Código da Macro': number;
    'Nome da Macro': string;
  }> {
    return this.dataSource.filteredData.map((item) => ({
      'Código da Macro': item.codEntidade,
      'Nome da Macro': item.nome,
    }));
  }

  private generateFileName(): string {
    return this.service.gerarNomeArquivo('Macros-de-postos_');
  }

  // ==================== Utility Methods ====================
  scrollToTopAnimated(): void {
    const scrollToTop = () => {
      const currentScroll =
        document.documentElement.scrollTop || document.body.scrollTop;
      if (currentScroll > 0) {
        window.requestAnimationFrame(scrollToTop);
        window.scrollTo(0, 0);
      }
    };
    scrollToTop();
  }
}
